import { useHotkeys } from 'react-hotkeys-hook'
import { useCallback, useLayoutEffect } from 'react'
import { APIElements } from '../../NonReact/constants/ApiConstants'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { redirectToServletWithBranding, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { useBranding } from '../../React/Common/Hooks/useBranding'
import { Branding } from '../../React/Common/API/BrandingAPI'
import type { AdminFilterValues } from '../Components/Molecules/AdminFilters/AdminFilters'
import {
    buildLegacyAdminEntityListQueryFromFilters,
    LegacyAdminListRedirectPayload,
    readLegacyAdminListRedirectPayload,
    storeLegacyAdminListRedirectPayload
} from '../Utils/adminListLegacyRedirect'

/**
 * Registers Ctrl+Shift+Alt+P to persist a legacy servlet redirect for this admin list, reload, and on each v2 visit
 * with a matching command redirect again using the stored query (or the last hotkey snapshot when re-armed).
 * localStorage is not cleared on redirect so returning to the v2 list later continues to send users to the classic
 * page until the payload is overwritten by the hotkey again or removed manually (see {@code clearLegacyAdminListRedirectPayload} in {@code adminListLegacyRedirect.ts}).
 *
 * @param servletCommand - Servlet command for this list (must match the stored payload for redirect to run).
 * @param filters - Current admin filter bar values (may be undefined or null until hydrated).
 * @param userScopeCmeId - User's default CM Environment id when the CME filter is unset.
 */
export function useAdminListLegacyServletSwitch(
    servletCommand: ServletCmds,
    filters: Nullable<AdminFilterValues>,
    userScopeCmeId: Definable<string>
): void {
    const branding: Nullable<Branding> = useBranding()

    useLayoutEffect((): void => {
        const payload: OnlyNullable<LegacyAdminListRedirectPayload> = readLegacyAdminListRedirectPayload()
        if (IsVarEmpty(payload)) return

        if (payload.servletCommand !== servletCommand) return

        const folder: string = (branding?.URLMapping ?? APIElements.AKIXI_2019).replace(/\//g, EMPTY_STRING)

        redirectToServletWithBranding(payload.servletCommand, folder, payload.legacyQuery)
    }, [branding?.URLMapping, servletCommand])

    const armLegacyRedirectAndReload = useCallback((): void => {
        const legacyQuery: Record<string, string> = buildLegacyAdminEntityListQueryFromFilters(
            filters ?? undefined,
            userScopeCmeId
        )

        storeLegacyAdminListRedirectPayload({
            version: 1,
            servletCommand,
            legacyQuery
        })

        window.location.reload()
    }, [filters, servletCommand, userScopeCmeId])

    useHotkeys(
        'ctrl+shift+alt+p',
        (event: KeyboardEvent): void => {
            event.preventDefault()
            armLegacyRedirectAndReload()
        },
        { enableOnFormTags: true, enableOnContentEditable: true },
        [armLegacyRedirectAndReload]
    )
}
